import React, { useState, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Building2, Loader2, Search, X, ChevronDown, Activity, Plus } from 'lucide-react';
import { AdminConsoleLayout } from '../components/AdminConsoleLayout';
import { desktopRibbonInputClass } from '../../../components/ribbon/DesktopRibbonControls';
import { AdminRibbonCtaButton, adminRibbonFilterBtnClass } from '../components/AdminRibbonCtaButton';
import { supabase } from '../../../lib/supabaseClient';
import { useAuth } from '../../../context/AuthContext';
import { joinOrganization } from '../../../services/organizationService';
import { RequireCapability } from '../../../components/auth/RequireCapability';
import { EmptyState } from '../../shared/components/EmptyState';
import { EnterpriseStatusBadge } from '../../shared/components/EnterpriseStatusBadge';
import { ErrorState } from '../../shared/components/ErrorState';
import { ModuleCard, ModuleLoadingState, ModulePageHeader } from '../../shared/components/ModulePageHeader';
import { normalizeOrganizationStatus, type NormalizedOrganizationStatus } from '../../org/utils/normalizeOrganizationStatus';

interface ClientRow {
  id: string;
  organization_id: string | null;
  organization_name: string | null;
  plan: string | null;
  status: string | null;
  active_users: number | null;
  max_users: number | null;
  monthly_value: number | null;
}

const currency = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' });

const getStatusLabel = (status: NormalizedOrganizationStatus): string => {
  if (status === 'TRIAL') return 'TRIAL';
  if (status === 'ACTIVE') return 'ATIVO';
  if (status === 'CANCELED' || status === 'SUSPENDED') return 'INATIVO';
  return 'NAO CONFIGURADO';
};

const getStatusTone = (status: NormalizedOrganizationStatus) => {
  if (status === 'TRIAL') return 'warning' as const;
  if (status === 'ACTIVE') return 'active' as const;
  if (status === 'CANCELED' || status === 'SUSPENDED') return 'risk' as const;
  return 'draft' as const;
};

const getPlanBadgeClass = (plan: string | null): string => {
  const normalized = String(plan || '').toLowerCase();

  if (normalized.includes('enterprise')) {
    return 'bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-950/40 dark:text-blue-300 dark:border-blue-900/60';
  }

  if (normalized.includes('professional')) {
    return 'bg-indigo-50 text-indigo-700 border-indigo-200 dark:bg-indigo-950/40 dark:text-indigo-300 dark:border-indigo-900/60';
  }

  if (normalized.includes('starter') || normalized.includes('basic')) {
    return 'bg-slate-100 text-slate-700 border-slate-200 dark:bg-zinc-800 dark:text-zinc-300 dark:border-zinc-700';
  }

  return 'bg-zinc-100 text-zinc-700 border-zinc-200 dark:bg-zinc-800 dark:text-zinc-300 dark:border-zinc-700';
};

export const AdminClientsPage: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user, refreshMemberships } = useAuth();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newOrgName, setNewOrgName] = useState('');
  const [newPlan, setNewPlan] = useState('starter');
  const [newMaxUsers, setNewMaxUsers] = useState(5);
  const [isCreating, setIsCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  const filtersWidget = useMemo(() => (
    <div className="flex flex-1 items-center justify-end gap-1.5">
      <AdminRibbonCtaButton
        label="Novo Workspace"
        title="Cadastrar nova organização na plataforma"
        onClick={() => setShowCreateModal(true)}
      />
      <div className="relative">
        <select
          className={`${adminRibbonFilterBtnClass} appearance-none cursor-pointer`}
          style={{ paddingLeft: 22, paddingRight: 22 }}
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          <option value="all">Todos status</option>
          <option value="active">Ativo</option>
          <option value="trial">Trial</option>
          <option value="suspended">Suspenso</option>
          <option value="expired">Expirado</option>
        </select>
        <Activity size={12} className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 text-[var(--text-secondary)]" />
        <ChevronDown size={10} className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-[var(--text-secondary)]" />
      </div>
      <div className="flex w-[220px] shrink-0 items-center">
        <div className="flex w-full items-center gap-2 rounded-[3px] border border-[var(--border-default)] bg-[var(--bg-surface)] px-2">
          <Search size={14} className="shrink-0 text-[var(--text-secondary)]" />
          <input
            type="text"
            placeholder="Buscar workspace..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className={`w-full ${desktopRibbonInputClass} border-0 bg-transparent px-0`}
          />
          {search && (
            <button onClick={() => setSearch('')} className="shrink-0 rounded p-0.5 hover:bg-[var(--button-hover)]">
              <X size={10} className="text-[var(--text-secondary)]" />
            </button>
          )}
        </div>
      </div>
    </div>
  ), [statusFilter, search]);

  const handleCreateClient = async () => {
    if (!newOrgName.trim()) return;
    setIsCreating(true);
    setCreateError(null);
    try {
      // Create organization
      const { data: org, error: orgError } = await supabase
        .from('organizations')
        .insert({ name: newOrgName.trim(), created_by: user?.id ?? null })
        .select('id')
        .single();
      if (orgError || !org) throw orgError || new Error('Falha ao criar organização');

      if (user?.id) {
        await joinOrganization(org.id, user.id);
        await refreshMemberships();
      }

      const licenseKey = `WF-${crypto.randomUUID().replace(/-/g, '').toUpperCase()}`;
      const { error: licError } = await supabase
        .from('client_licenses')
        .insert({
          organization_id: org.id,
          plan: newPlan,
          status: 'trial',
          max_users: newMaxUsers,
          active_users: 0,
          monthly_value: 0,
          license_key: licenseKey,
        });
      if (licError) {
        await supabase.from('organizations').delete().eq('id', org.id);
        throw licError;
      }

      await queryClient.invalidateQueries({ queryKey: ['admin-clients'] });
      setShowCreateModal(false);
      setNewOrgName('');
      setNewPlan('starter');
      setNewMaxUsers(5);
      navigate(`/administracao/clientes/${org.id}`);
    } catch (err: any) {
      setCreateError(err?.message || 'Erro ao criar cliente.');
    } finally {
      setIsCreating(false);
    }
  };

  const clientsQuery = useQuery({
    queryKey: ['admin-clients'],
    queryFn: async (): Promise<ClientRow[]> => {
      const { data, error } = await supabase
        .from('client_licenses')
        .select('id, organization_id, plan, status, active_users, max_users, monthly_value, organizations(name)')
        .order('created_at', { ascending: false });

      if (error) {
        throw error;
      }

      return (data || []).map((row: any) => ({
        id: String(row.id),
        organization_id: row.organization_id ?? null,
        organization_name: row.organizations?.name ?? null,
        plan: row.plan ?? null,
        status: row.status ?? null,
        active_users: row.active_users ?? null,
        max_users: row.max_users ?? null,
        monthly_value: row.monthly_value ?? null,
      }));
    },
  });

  return (
    <RequireCapability capability="admin.access" realRoleOnly>
      <AdminConsoleLayout ribbonSlots={{ afterGroups: filtersWidget }}>
      <div className="enterprise-page h-full overflow-y-auto">
        <ModulePageHeader
          icon={Building2}
          title="Workspaces"
          subtitle="Empresas cadastradas na plataforma — planos, limites de usuários e instâncias"
        />

        {clientsQuery.isPending && <ModuleLoadingState label="Carregando workspaces" />}

        {clientsQuery.isError && (
          <ErrorState message="Nao foi possivel carregar os workspaces da base." onRetry={clientsQuery.refetch} />
        )}

        {!clientsQuery.isPending && !clientsQuery.isError && !clientsQuery.data?.length && (
          <EmptyState
            title="Nenhum workspace encontrado"
            description="Nao ha empresas cadastradas com licenca ativa no momento."
            ctaLabel="Tentar novamente"
            onCta={() => clientsQuery.refetch()}
          />
        )}

        {!clientsQuery.isPending && !clientsQuery.isError && Boolean(clientsQuery.data?.length) && (() => {
          const filtered = (clientsQuery.data ?? []).filter((c) => {
            const matchSearch = !search || (c.organization_name ?? '').toLowerCase().includes(search.toLowerCase());
            const matchStatus = statusFilter === 'all' || c.status === statusFilter;
            return matchSearch && matchStatus;
          });
          return (
            <ModuleCard
              title={`Empresas cadastradas (${filtered.length})`}
            >
              <div className="enterprise-table-shell">
                <table className="w-full text-left text-sm">
                  <thead className="enterprise-table-header">
                    <tr>
                      <th className="enterprise-table-head-cell">Workspace</th>
                      <th className="enterprise-table-head-cell">Plano</th>
                      <th className="enterprise-table-head-cell">Status</th>
                      <th className="enterprise-table-head-cell text-right">Usuarios</th>
                      <th className="enterprise-table-head-cell text-right">Mensalidade</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((client) => {
                      const normalizedStatus = normalizeOrganizationStatus({ status: client.status });
                      const planLabel = client.plan ? String(client.plan).toUpperCase() : 'NAO CONFIGURADO';
                      const activeUsers = client.active_users ?? 0;
                      const maxUsers = client.max_users ?? 0;
                      return (
                        <tr
                          key={client.id}
                          className="enterprise-table-row cursor-pointer hover:bg-[var(--bg-elevated)]"
                          onClick={() => client.organization_id && navigate(`/administracao/clientes/${client.organization_id}`)}
                        >
                          <td className="enterprise-table-cell font-medium">{client.organization_name || 'Org sem nome'}</td>
                          <td className="enterprise-table-cell">
                            <span className={`inline-flex px-2.5 py-1 rounded-md border text-xs font-semibold ${getPlanBadgeClass(client.plan)}`}>
                              {planLabel}
                            </span>
                          </td>
                          <td className="enterprise-table-cell">
                            <EnterpriseStatusBadge label={getStatusLabel(normalizedStatus)} tone={getStatusTone(normalizedStatus)} />
                          </td>
                          <td className="enterprise-table-cell text-right tabular-nums">{activeUsers}/{maxUsers}</td>
                          <td className="enterprise-table-cell text-right font-medium tabular-nums">
                            {currency.format(Number(client.monthly_value || 0))}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </ModuleCard>
          );
        })()}
      </div>

      {/* Modal: Novo Workspace (portal + fundo opaco — mesmo padrão AdminUsersPage) */}
      {showCreateModal && createPortal(
        <div
          className="fixed inset-0 z-[200] flex items-center justify-center bg-black/60 backdrop-blur-sm"
          role="presentation"
          onClick={() => !isCreating && setShowCreateModal(false)}
        >
          <div
            className="enterprise-card-surface w-[440px] max-w-[calc(100vw-2rem)] p-5 shadow-xl flex flex-col gap-4"
            role="dialog"
            aria-labelledby="new-workspace-title"
            aria-modal="true"
            onClick={(e) => e.stopPropagation()}
          >
            <div>
              <div className="flex items-center gap-2 mb-1">
                <Building2 size={15} className="enterprise-text-secondary" />
                <h3 id="new-workspace-title" className="text-sm font-semibold enterprise-text-primary">
                  Novo Workspace
                </h3>
              </div>
              <p className="text-xs enterprise-text-secondary">
                Crie uma nova organização e licença na plataforma.
              </p>
            </div>

            <div className="space-y-3">
              <div>
                <label className="block text-[11px] enterprise-text-secondary mb-1">Nome da Organização</label>
                <input
                  type="text"
                  placeholder="Ex: Empresa ABC Ltda"
                  className="enterprise-input w-full text-sm"
                  value={newOrgName}
                  onChange={(e) => setNewOrgName(e.target.value)}
                  autoFocus
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-[11px] enterprise-text-secondary mb-1">Plano</label>
                  <select className="enterprise-input w-full text-sm" value={newPlan} onChange={(e) => setNewPlan(e.target.value)}>
                    <option value="starter">Starter</option>
                    <option value="professional">Professional</option>
                    <option value="enterprise">Enterprise</option>
                  </select>
                </div>
                <div>
                  <label className="block text-[11px] enterprise-text-secondary mb-1">Máx. Usuários</label>
                  <input
                    type="number"
                    min={1}
                    className="enterprise-input w-full text-sm"
                    value={newMaxUsers}
                    onChange={(e) => setNewMaxUsers(parseInt(e.target.value, 10) || 5)}
                  />
                </div>
              </div>
              {createError && <p className="text-xs text-red-500">{createError}</p>}
            </div>

            <div className="flex justify-end gap-2 pt-1 border-t border-[var(--border-default)]">
              <button
                type="button"
                className="enterprise-button-secondary px-3 py-1.5 text-xs"
                onClick={() => setShowCreateModal(false)}
                disabled={isCreating}
              >
                Cancelar
              </button>
              <button
                type="button"
                className="enterprise-btn-primary px-3 py-1.5 text-xs flex items-center gap-1.5"
                onClick={handleCreateClient}
                disabled={isCreating || !newOrgName.trim()}
              >
                {isCreating ? <Loader2 size={13} className="animate-spin" /> : <Plus size={13} />}
                {isCreating ? 'Criando...' : 'Criar'}
              </button>
            </div>
          </div>
        </div>,
        document.body,
      )}
      </AdminConsoleLayout>
    </RequireCapability>
  );
};
